import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'react-hot-toast';
import AppLoadingState from './common/AppLoadingState';
import { AppPageShell, Button, PageHeader } from './common/ui';

const PhoneNumbers = () => {
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseModal, setPurchaseModal] = useState(false);
  const [configureModal, setConfigureModal] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [purchaseForm, setPurchaseForm] = useState({
    area_code: '415',
    country_code: 'US',
    phone_number: ''
  });
  const [configForm, setConfigForm] = useState({
    prompt: '',
    webhook: null,
    voice: null,
    background_track: null,
    metadata: null,
    dynamic_data: null,
    interruption_threshold: null,
    first_sentence: null,
    voice_settings: null,
    record: false,
    max_duration: 30,
    custom_tools: null,
    model: 'enhanced',
    transfer_list: null,
    transfer_phone_number: null,
    temperature: null,
    pathway_id: null,
    analysis_schema: null,
    language: 'ENG',
    timezone: 'America/Los_Angeles',
    keywords: null,
    request_data: null,
    analysis_prompt: null,
    analysis_preset: null,
    summary_prompt: null,
    language_detection_period: null,
    language_detection_options: null,
    block_interruptions: false,
    improve_accuracy: false,
    start_node_id: null,
    pronunciation_guide: null,
    pathway_version: null,
    ignore_button_press: null,
    webhook_events: null,
    noise_cancellation: false,
    status_callback: null,
    skip_url_update: false
  });

  useEffect(() => {
    fetchNumbers();
  }, []);

  const fetchNumbers = async () => {
    try {
      setLoading(true);
      const response = await api.getNumbers();
      setNumbers(response.inbound_numbers || []);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch numbers');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    try {
      const response = await api.purchaseNumber(purchaseForm);
      toast.success('Phone number purchased successfully');
      setPurchaseModal(false);
      fetchNumbers();
    } catch (error) {
      toast.error(error.message || 'Failed to purchase number');
    }
  };

  const handleConfigure = async (e) => {
    e.preventDefault();
    try {
      await api.updateNumber(selectedNumber.phone_number, configForm);
      toast.success('Phone number configured successfully');
      setConfigureModal(false);
      fetchNumbers();
    } catch (error) {
      toast.error(error.message || 'Failed to configure number');
    }
  };

  const openConfigureModal = async (number) => {
    try {
      const details = await api.getNumberDetails(number.phone_number);
      setSelectedNumber(number);
      setConfigForm({
        prompt: details.prompt || '',
        webhook: details.webhook,
        voice: details.voice,
        background_track: details.background_track,
        metadata: details.metadata,
        dynamic_data: details.dynamic_data,
        interruption_threshold: details.interruption_threshold,
        first_sentence: details.first_sentence || '',
        voice_settings: details.voice_settings,
        record: details.record || false,
        max_duration: details.max_duration || 30,
        custom_tools: details.custom_tools,
        model: details.model || 'enhanced',
        transfer_list: details.transfer_list,
        transfer_phone_number: details.transfer_phone_number,
        temperature: details.temperature,
        pathway_id: details.pathway_id,
        analysis_schema: details.analysis_schema,
        language: details.language || 'ENG',
        timezone: details.timezone || 'America/Los_Angeles',
        keywords: details.keywords,
        request_data: details.request_data,
        analysis_prompt: details.analysis_prompt,
        analysis_preset: details.analysis_preset,
        summary_prompt: details.summary_prompt,
        language_detection_period: details.language_detection_period,
        language_detection_options: details.language_detection_options,
        block_interruptions: details.block_interruptions || false,
        improve_accuracy: details.improve_accuracy || false,
        start_node_id: details.start_node_id,
        pronunciation_guide: details.pronunciation_guide,
        pathway_version: details.pathway_version,
        ignore_button_press: details.ignore_button_press,
        webhook_events: details.webhook_events,
        noise_cancellation: details.noise_cancellation || false,
        status_callback: details.status_callback,
        skip_url_update: details.skip_url_update || false
      });
      setConfigureModal(true);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch number details');
    }
  };

  return (
    <AppPageShell maxWidthClass="max-w-6xl">
      <PageHeader
        eyebrow="Telephony"
        title="Phone numbers"
        description="Provision inbound numbers and open configuration for prompts, webhooks, and routing."
        actions={
          <Button onClick={() => setPurchaseModal(true)}>Purchase number</Button>
        }
      />

      {loading ? (
        <AppLoadingState layout="compact" showLabel={false} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {numbers.map((number) => (
            <div key={number.phone_number} className="border rounded-lg p-4 shadow">
              <h3 className="text-lg font-semibold">{number.phone_number}</h3>
              <p className="text-gray-600 text-sm mt-2">Created: {new Date(number.created_at).toLocaleDateString()}</p>
              <div className="mt-4">
                <button
                  onClick={() => openConfigureModal(number)}
                  className="text-blue-500 hover:text-blue-600 mr-4"
                >
                  Configure
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Purchase Modal */}
      {purchaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Purchase New Number</h2>
            <form onSubmit={handlePurchase}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Area Code</label>
                <input
                  type="text"
                  value={purchaseForm.area_code}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, area_code: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Country Code</label>
                <select
                  value={purchaseForm.country_code}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, country_code: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Specific Number (Optional)</label>
                <input
                  type="text"
                  value={purchaseForm.phone_number}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, phone_number: e.target.value })}
                  placeholder="+1XXXXXXXXXX"
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPurchaseModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Purchase
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Configure Modal */}
      {configureModal && selectedNumber && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Configure {selectedNumber.phone_number}</h2>
            <form onSubmit={handleConfigure} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Basic Settings */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Prompt</label>
                    <textarea
                      value={configForm.prompt || ''}
                      onChange={(e) => setConfigForm({ ...configForm, prompt: e.target.value })}
                      className="w-full border rounded px-3 py-2 h-32"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">First Sentence</label>
                    <input
                      type="text"
                      value={configForm.first_sentence || ''}
                      onChange={(e) => setConfigForm({ ...configForm, first_sentence: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Voice</label>
                    <input
                      type="text"
                      value={configForm.voice || ''}
                      onChange={(e) => setConfigForm({ ...configForm, voice: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Background Track</label>
                    <select
                      value={configForm.background_track || 'none'}
                      onChange={(e) => setConfigForm({ ...configForm, background_track: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="none">None</option>
                      <option value="office">Office</option>
                      <option value="cafe">Cafe</option>
                      <option value="restaurant">Restaurant</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Model</label>
                    <select
                      value={configForm.model || 'enhanced'}
                      onChange={(e) => setConfigForm({ ...configForm, model: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="enhanced">Enhanced</option>
                      <option value="turbo">Turbo</option>
                      <option value="base">Base</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Language</label>
                    <input
                      type="text"
                      value={configForm.language || 'ENG'}
                      onChange={(e) => setConfigForm({ ...configForm, language: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Max Duration (seconds)</label>
                    <input
                      type="number"
                      value={configForm.max_duration || 30}
                      onChange={(e) => setConfigForm({ ...configForm, max_duration: parseInt(e.target.value) })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                </div>

                {/* Advanced Settings */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Webhook URL</label>
                    <input
                      type="text"
                      value={configForm.webhook || ''}
                      onChange={(e) => setConfigForm({ ...configForm, webhook: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Transfer Phone Number</label>
                    <input
                      type="text"
                      value={configForm.transfer_phone_number || ''}
                      onChange={(e) => setConfigForm({ ...configForm, transfer_phone_number: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Pathway ID</label>
                    <input
                      type="text"
                      value={configForm.pathway_id || ''}
                      onChange={(e) => setConfigForm({ ...configForm, pathway_id: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Timezone</label>
                    <input
                      type="text"
                      value={configForm.timezone || 'America/Los_Angeles'}
                      onChange={(e) => setConfigForm({ ...configForm, timezone: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  {/* Checkboxes */}
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={configForm.record}
                        onChange={(e) => setConfigForm({ ...configForm, record: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium">Record Call</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={configForm.block_interruptions}
                        onChange={(e) => setConfigForm({ ...configForm, block_interruptions: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium">Block Interruptions</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={configForm.improve_accuracy}
                        onChange={(e) => setConfigForm({ ...configForm, improve_accuracy: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium">Improve Accuracy</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={configForm.noise_cancellation}
                        onChange={(e) => setConfigForm({ ...configForm, noise_cancellation: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium">Noise Cancellation</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setConfigureModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Save Configuration
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppPageShell>
  );
};

export default PhoneNumbers; 